import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown, Leaf } from 'lucide-react';

import { Badge } from '@/components/badge';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { DISTANCE_UNITS, MODE_WATER, UNIT_CO2 } from '@/lib/constants';
import { EmissionsCalculationModel } from '@/lib/models/emissions/types';
import { LocationDictionaryModel } from '@/lib/models/location/types';
import { ShipmentCargo } from '@/lib/models/shipment/types';
import { VehicleDictionaryModel } from '@/lib/models/vehicle/types';
import { getSummedEmissionSegments } from '@/utils/emissions';
import { formatDistance } from '@/utils/format';
import { formatEmissionsNum } from '@/utils/format/emissions';
import { convertModeType, convertSegmentModeVehicleType } from '@/utils/format/mode';
import { getPreferredIdentifier } from '@/utils/helpers';
import { roundFloatToFixed } from '@/utils/helpers/maths';
import { tv } from '@/utils/styles';

import { EmissionsAssumptions } from '../assumptions';
import { EmissionsSegmentDetailsLocation } from './location';

export interface EmissionsSegmentBreakdownProps {
  id: string;
  data: EmissionsCalculationModel;
  cargo?: ShipmentCargo[];
  vehicles: VehicleDictionaryModel;
  locations: LocationDictionaryModel;
}

const EmissionsSegmentBreakdown = ({ id, data, cargo = [], vehicles, locations }: EmissionsSegmentBreakdownProps) => {
  const firstSegment = data.segments[0];
  const { vehicleId, type: modeType } = firstSegment;

  const vehicle = vehicleId ? vehicles[vehicleId] : undefined;
  const vehicleIdentifier = vehicle?.identifiers ? getPreferredIdentifier(vehicle?.identifiers) : null;
  const isWater = firstSegment.type === MODE_WATER;
  const vehicleType = convertSegmentModeVehicleType(modeType);
  const vehicleIcon = convertModeType(modeType);

  const fromLocation = locations[data.fromId];
  const toLocation = locations[data.toId];

  const totals = getSummedEmissionSegments(data.segments);

  const {
    accordionWrapper,
    accordionButton,
    sectionTitle,
    dl,
    highlight,
    emissionsBlock,
    emissionsValue,
    showMoreButton,
  } = styles();

  return (
    <Accordion.Item key={id} value={id}>
      <div className={accordionWrapper()}>
        <Accordion.Trigger asChild>
          <button type="button" className={accordionButton()}>
            <div>
              <div className="flex gap-2 text-baseSm">
                <EmissionsSegmentDetailsLocation location={fromLocation} /> <span className="px-1">→</span>
                <EmissionsSegmentDetailsLocation location={toLocation} />
              </div>
              <div className="mt-2 flex gap-2">
                {modeType && (
                  <Badge className="flex items-center gap-1">
                    <Icon name={vehicleIcon} color="black" size="3" />
                    {vehicleType}
                  </Badge>
                )}
                {isWater && vehicleIdentifier && <Badge>IMO {vehicleIdentifier?.value}</Badge>}
                <Badge>{formatDistance(data.distance || 0, DISTANCE_UNITS.KM)}</Badge>
              </div>
            </div>
            <div className={emissionsBlock()}>
              <div className={emissionsValue()}>
                <div className="flex items-center text-baseLg font-semibold">
                  <Leaf className="mr-1 size-3.5 fill-green-300" /> {formatEmissionsNum(data.co2e.wtw)}
                </div>
                <span className="text-xs font-medium text-grey-600">Emissions ({UNIT_CO2})</span>
              </div>
              <span className={showMoreButton()}>
                <ChevronDown className="size-4 text-grey-500 group-hover:text-grey-800" />
              </span>
            </div>
          </button>
        </Accordion.Trigger>
        <Accordion.Content>
          <div className="border-t border-grey-100 bg-grey-50 p-4">
            <Heading variant="h4" className={sectionTitle()}>
              Methodology
            </Heading>
            <div className="mb-6">
              <dl className={dl()}>
                <dt>GLEC</dt>
                <dd>
                  SmartFreight 2024 - <span className={highlight()}>ISO 14083</span>
                </dd>
              </dl>
              <dl className={dl()}>
                <dt>Source</dt>
                <dd>
                  <span className={highlight()}>GLEC 2024</span>, version <span className={highlight()}>3.1</span>,{' '}
                  <span className={highlight()}>Dec-04</span>
                </dd>
              </dl>
              <dl className={dl()}>
                <dt>Factors</dt>
                <dd>
                  <span className={highlight()}>WTW</span>{' '}
                  <span className={highlight()}>
                    {roundFloatToFixed(totals.intensity)} {UNIT_CO2}/{DISTANCE_UNITS.TKM}
                  </span>
                </dd>
              </dl>
            </div>
            <Heading variant="h4" className={sectionTitle()}>
              Assumptions
            </Heading>
            <div className="mb-6">
              <EmissionsAssumptions mode={modeType} vehicle={vehicle} cargo={cargo} distance={data.distance} />
            </div>
          </div>
        </Accordion.Content>
      </div>
    </Accordion.Item>
  );
};

const styles = tv({
  slots: {
    accordionWrapper:
      'mb-4 overflow-hidden rounded-md border border-grey-200 bg-white hover:border-grey-400 hover:shadow',
    accordionButton: 'group flex w-full cursor-pointer justify-between px-5 py-4 leading-normal',
    sectionTitle: 'mb-3 text-baseSm font-semibold leading-snug',
    dl: 'mb-2 grid grid-cols-[150px_1fr] text-baseSm [&_dt]:font-medium',
    highlight: 'bg-lightBlue-50 px-1 py-0.5 font-mono text-[85%] text-lightBlue-900',
    emissionsBlock: 'flex items-center gap-4 py-1',
    emissionsValue: 'flex flex-col items-end gap-1 whitespace-nowrap leading-none',
    showMoreButton: 'grid size-8 place-items-center rounded-md bg-grey-100 group-hover:bg-grey-300',
  },
  variants: {
    isValue: {
      true: {
        highlight: 'bg-green-100/50 text-green-800',
      },
      false: null,
    },
  },
});

export { EmissionsSegmentBreakdown };
